<template>
  <main class="about">
    <AppSection
      class="hero"
      :data="{
        img: { src: about.hero.img.src },
      }"
    >
      <div class="hero__container">
        <AppHeadline
          class="hero__headline"
          :data="{
            content: about.hero.title,
            type: 2,
          }"
        />

        <span class="section__line hero__line"></span>

        <div class="hero__text">
          <AppParagraph
            class="text-content"
            :data="{
              content: about.hero.content[0],
            }"
          />
          <AppParagraph
            class="text-content"
            :data="{
              content: about.hero.content[1],
            }"
            style="opacity: 0"
          />
        </div>
      </div>
    </AppSection>

    <AppSection
      class="intro"
      :data="{
        img: { src: '/assets/video/about_0.jpg' },
        video: { src: '/assets/video/about_0.m4v' },
      }"
    >
      <span
        class="intro__sphere"
        :style="{
          backgroundImage: `url('/assets/svg/sphere${isSafari() ? '-safari' : ''}.svg')`,
        }"
      ></span>

      <div class="intro__container">
        <AppHeadline
          class="intro__headline"
          :data="{
            content: about.intro.title,
            type: 2,
          }"
        />

        <span class="section__line intro__line"></span>

        <AppParagraph
          class="intro__text"
          :data="{
            content: about.intro.content,
          }"
        />
      </div>

      <div class="intro__container">
        <ul>
          <li
            v-for="(item, i) in about.intro.list"
            :key="`item-${i}`"
            class="list__items"
          >
            <AppHeadline
              class="item__headline"
              :data="{
                content: item.firstLine,
                type: 3,
              }"
            />
            <AppParagraph
              :data="{
                content: item.secondLine,
              }"
            />
          </li>
        </ul>
      </div>
    </AppSection>

    <NewsPreview title="OUR THINKING" :haveButton="true"/>

    <AppFooter />
  </main>
</template>

<script>
import { TweenMax, TimelineMax, Power4 } from 'gsap/TweenMax';
import IScroll from 'iscroll/build/iscroll-probe';
import scrollMagic from '@/scripts/scrollMagic';
import { isMobile } from '@/scripts/utils';

import AppSection from '@/components/AppSection.vue';
import AppHeadline from '@/components/AppHeadline.vue';
import AppParagraph from '@/components/AppParagraph.vue';
import AppFooter from '@/components/AppFooter.vue';
import NewsPreview from '@/components/NewsPreview.vue';

import about from '@/assets/data/about.json';

export default {
  name: 'About',
  components: {
    AppSection,
    AppHeadline,
    AppParagraph,
    AppFooter,
    NewsPreview,
  },
  data() {
    return {
      about,
      controller: null,
    };
  },
  mounted() {
    this.init();
    this.enter();

    // init controller
    this.controller = new scrollMagic.Controller({ container: '.app__container--about' });
    this.hero();
    this.intro();
    if (isMobile()) {
      // configure iScroll
      this.myScroll = new IScroll('.app__container', {
        scrollX: false,
        scrollY: true,
        scrollbars: true,
        useTransform: false,
        useTransition: false,
        probeType: 3,
        click: true,
        disablePointer: true,
        disableTouch: false,
        disableMouse: false,
      });

      this.controller.scrollPos(() => -this.myScroll.y);

      this.handlerScroll = () => this.controller.update(true);
      this.myScroll.on('scroll', this.handlerScroll);
    }
  },
  methods: {
    isSafari() {
      return navigator.userAgent.indexOf('Safari') > 0 && navigator.userAgent.indexOf('Chrome') < 0;
    },
    init() {
      TweenMax.set('.hero .hero__headline', { autoAlpha: 0, y: 50 });
      TweenMax.set('.hero .hero__line', { scaleY: 0, transformOrigin: '50% 0' });
      TweenMax.set('.hero .hero__text', { autoAlpha: 0, y: 50 });
      TweenMax.set('.hero .section__bg', {
        height: 'calc(100vh + 180px)',
        y: -100,
        autoAlpha: 0,
        scale: 1.2,
      });
      TweenMax.set('.hero__container', { y: 150 });
      TweenMax.set('.intro__sphere', {
        webkitClipPath: 'inset(100% 0.01% 0% 0.02%)',
        clipPath: 'inset(100% 0.01% 0% 0.02%)',
      });
      TweenMax.set('.intro .intro__headline', { autoAlpha: 0, y: 50 });
      TweenMax.set('.intro .intro__line', { scaleY: 0, transformOrigin: '50% 0' });
      TweenMax.set('.intro .intro__text', { autoAlpha: 0, y: 50 });
      TweenMax.staggerTo('.intro .list__items', 0, {
        autoAlpha: 0,
        cycle: {
          y: i => i === 0 ? 80 : i * 80,
        },
      }, 0);
    },
    enter() {
      const tl = new TimelineMax({ delay: 0.5 });

      tl.addLabel('start')
        .to('.hero .section__bg', 2, {
          autoAlpha: 1,
          scale: 1,
          ease: Power4.easeOut,
        })
        .to('.hero__headline', 1, {
          y: 0,
          autoAlpha: 1,
          ease: Power4.easeOut,
          clearProps: 'all',
        }, 'start')
        .to('.hero__line', 1, {
          scaleY: 1,
          ease: Power4.easeOut,
        }, 'start+=0.25')
        .to('.hero__text', 1, {
          autoAlpha: 1,
          y: 0,
          ease: Power4.easeOut,
          clearProps: 'all',
        }, 'start+=0.5');
    },
    hero() {
      const text = Array.from(this.$el.querySelectorAll('.hero .text-content'));
      const tl = new TimelineMax();

      tl.addLabel('start')
        .to('.hero .section__bg', 1, {
          y: -180,
          ease: Power4.easeOut,
        }, 'start')
        .to('.hero__container', 1, {
          y: 20,
          ease: Power4.easeOut,
        }, 'start')
        .to(text[0], 0.8, {
          autoAlpha: 0,
          y: -50,
          ease: Power4.easeOut,
        }, 'start')
        .fromTo(text[1], 0.8, {
          autoAlpha: 0,
          y: 50,
        }, {
          autoAlpha: 1,
          y: 0,
          ease: Power4.easeOut,
        }, 'start+=0.5');


      // create a scene
      this.heroScene = new scrollMagic.Scene({
        duration: 800,
        offset: 0,
      })
        .setTween(tl)
        .setPin('.about')
        .addTo(this.controller);
    },
    intro() {
      const item = Array.from(this.$el.querySelectorAll('.intro .list__items'));
      TweenMax.set(item[2], { y: 160 + item[2].getBoundingClientRect().height });

      const tl = new TimelineMax({ delay: 1 });

      tl.addLabel('start')
        .to('.intro__headline', 1, {
          y: 0,
          autoAlpha: 1,
          ease: Power4.easeOut,
          clearProps: 'all',
        }, 'start')
        .to('.intro__sphere', 2, {
          webkitClipPath: 'inset(0.03% 0.01% 0% 0.02%)',
          clipPath: 'inset(0.03% 0.01% 0% 0.02%)',
          ease: Power4.easeOut,
        }, 'start')
        .to('.intro__line', 1, {
          scaleY: 1,
          ease: Power4.easeOut,
        }, 'start+=0.25')
        .to('.intro__text', 1, {
          autoAlpha: 1,
          y: 0,
          ease: Power4.easeOut,
          clearProps: 'all',
        }, 'start+=0.5')
        .to('.intro__headline', 1, {
          y: -50,
          autoAlpha: 0,
          ease: Power4.easeOut,
        }, 'start+=1.5')
        .to('.intro__sphere', 1, {
          rotation: 90,
          ease: Power4.easeOut,
        }, 'start+=1.5')
        .to('.intro__line', 1, {
          autoAlpha: 0,
          y: -50,
          ease: Power4.easeOut,
        }, 'start+=1.5')
        .to('.intro__text', 1, {
          autoAlpha: 0,
          y: -50,
          ease: Power4.easeOut,
        }, 'start+=1.5')
        .to('.intro__sphere', 3.5, {
          scale: 1.1,
          ease: Power4.easeOut,
        }, 'start+=2.85')
        .to(item[0], 1, {
          autoAlpha: 1,
          y: 0,
          ease: Power4.easeOut,
        }, 'start+=2.5')
        .to(item[0], 1, {
          y: -80 - item[0].getBoundingClientRect().height,
          ease: Power4.easeOut,
        }, 'start+=3.65')
        .to(item[1], 1, {
          autoAlpha: 1,
          y: 0,
          ease: Power4.easeOut,
        }, 'start+=3.75')
        .to(item[2], 1, {
          autoAlpha: 1,
          y: 80 + item[2].getBoundingClientRect().height,
          ease: Power4.easeOut,
        }, 'start+=4.75');

      // create a scene
      this.introScene = new scrollMagic.Scene({
        duration: 4000,
        offset: 800 + window.innerHeight,
      })
        .setTween(tl)
        .setPin('.about')
        .addTo(this.controller);
    },
  },
  beforeDestroy() {
    if (isMobile() && navigator.userAgent.indexOf('OS 13_') < 0 && navigator.userAgent.indexOf('Safari') > 0) {
      this.myScroll.off('scroll', this.handlerScroll);
      this.myScroll.destroy();
      this.myScroll = null;
    }
    this.controller.destroy();
    this.controller = null;
    this.heroScene.destroy();
    this.heroScene = null;
    this.introScene.destroy();
    this.introScene = null;
  },
};
</script>

<style lang="scss" scoped>
.about {
  .hero,
  .hero__container,
  .intro__container {
    align-items: center;
    justify-content: center;
    flex-flow: column nowrap;
    text-align: center;

    padding: 0 20px;
  }

  .hero {
    &__text {
      position: relative;
      max-width: 400px;
      text-align: center;

       & .text-content:last-child {
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  .hero__container,
  .intro__container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    display: flex;
  }

  .intro {
    position: relative;

    &__sphere {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 75vw;
      height: 75vw;

      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;

      transform: translateX(-50%) translateY(-50%);

      z-index: -1;

      @include mq(ipadL) {
        width: 75vh;
        height: 75vh;
      }
    }

    .item__headline {
      margin-bottom: 15px;

      @include mq(ipadL) {
        width: 75vw;
      }
    }

    &__text {
      position: relative;
      max-width: 400px;
      text-align: center;
    }

    ul {
      position: relative;
    }

    ul li {
      position: absolute;
      top: 0;
      left: 0;

      &:first-child {
        position: relative;
      }
    }
  }
}
</style>
